import { useEffect, useState } from "react"

import { zodResolver } from "@hookform/resolvers/zod"
import { useForm } from "react-hook-form"
import { useTranslation } from "react-i18next"
import { z } from "zod"

import { Import, Trash2 } from "@/components/icons"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Spinner } from "@/components/ui/spinner"

import { useDeleteTemplateMutation } from "../hooks/use-delete-template-mutation"
import { useImportTemplateMutation } from "../hooks/use-import-template-mutation"
import { useTemplatesQuery } from "../hooks/use-templates-query"

const createImportTemplateFormSchema = (t: (key: string) => string) =>
  z.object({
    templateId: z.string().min(1, t("pages.ai_parser.validation.template_select_required")),
  })

type ImportTemplateDialogProps = {
  isOpen: boolean
  onOpenChange: (open: boolean) => void
}

export function ImportTemplateDialog({ isOpen, onOpenChange }: ImportTemplateDialogProps) {
  const { t } = useTranslation()
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false)

  const templatesQuery = useTemplatesQuery()
  const deleteTemplateMutation = useDeleteTemplateMutation()
  const importTemplateMutation = useImportTemplateMutation()

  const isPending = importTemplateMutation.isPending
  const templates = templatesQuery.data || []

  const importTemplateFormSchema = createImportTemplateFormSchema(t)
  type ImportTemplateFormValues = z.infer<typeof importTemplateFormSchema>

  const form = useForm<ImportTemplateFormValues>({
    resolver: zodResolver(importTemplateFormSchema),
    defaultValues: {
      templateId: "",
    },
  })

  const selectedTemplateId = form.watch("templateId")
  const isTemplateSelected = Boolean(selectedTemplateId && selectedTemplateId.length > 0)

  const selectedTemplate = templates.find((template) => template.id === selectedTemplateId)
  const personalTemplates = templates.filter((t) => t.type === "personal")
  const companyTemplates = templates.filter((t) => t.type === "company")

  const handleSubmit = (data: ImportTemplateFormValues) => {
    const template = templates.find((template) => template.id === data.templateId)

    if (!template) {
      return
    }

    importTemplateMutation.mutate(
      {
        templateId: data.templateId,
        templateTitle: template.title,
      },
      {
        onSuccess: () => {
          form.reset()
          onOpenChange(false)
        },
      }
    )
  }

  const deleteTemplate = () => {
    if (!selectedTemplate) {
      return
    }

    deleteTemplateMutation.mutate(
      {
        templateId: selectedTemplateId,
        type: selectedTemplate.type,
      },
      {
        onSuccess: () => {
          setIsDeleteDialogOpen(false)
        },
      }
    )
  }

  const cancelDeleteTemplate = () => {
    setIsDeleteDialogOpen(false)
  }

  useEffect(() => {
    form.reset()
  }, [isOpen])

  return (
    <>
      <Dialog open={isOpen} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>{t("pages.ai_parser.import_template_dialog.title")}</DialogTitle>
            <DialogDescription>
              {t("pages.ai_parser.import_template_dialog.description")}
            </DialogDescription>
          </DialogHeader>
          <Form {...form}>
            <form className="space-y-4" onSubmit={form.handleSubmit(handleSubmit)}>
              <FormField
                control={form.control}
                name="templateId"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t("pages.ai_parser.import_template_dialog.field_title")}</FormLabel>
                    <FormControl>
                      <div className="space-y-2">
                        <Select
                          disabled={isPending || templatesQuery.isLoading}
                          value={field.value}
                          onValueChange={field.onChange}
                        >
                          <SelectTrigger className="w-full">
                            <SelectValue
                              placeholder={t(
                                "pages.ai_parser.import_template_dialog.select_placeholder"
                              )}
                            />
                          </SelectTrigger>
                          <SelectContent>
                            {personalTemplates.length > 0 && (
                              <>
                                <div className="text-muted-foreground px-2 py-1.5 text-xs font-semibold">
                                  {t("pages.ai_parser.import_template_dialog.personal")}
                                </div>
                                {personalTemplates.map((template) => (
                                  <SelectItem key={template.id} value={template.id}>
                                    <div className="flex w-full items-center justify-between">
                                      <span>{template.title}</span>
                                    </div>
                                  </SelectItem>
                                ))}
                              </>
                            )}
                            {companyTemplates.length > 0 && (
                              <>
                                <div className="text-muted-foreground px-2 py-1.5 text-xs font-semibold">
                                  {t("pages.ai_parser.import_template_dialog.company")}
                                </div>
                                {companyTemplates.map((template) => (
                                  <SelectItem key={template.id} value={template.id}>
                                    <div className="flex w-full items-center justify-between">
                                      <span>{template.title}</span>
                                    </div>
                                  </SelectItem>
                                ))}
                              </>
                            )}
                          </SelectContent>
                        </Select>
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {templatesQuery.isLoading && (
                <div className="flex items-center justify-center py-4">
                  <Spinner />
                  <span className="text-muted-foreground ml-2 text-sm">
                    {t("pages.ai_parser.import_template_dialog.loading")}
                  </span>
                </div>
              )}

              {templates.length === 0 && !templatesQuery.isLoading && (
                <div className="text-muted-foreground py-4 text-center text-sm">
                  {t("pages.ai_parser.import_template_dialog.no_templates")}
                </div>
              )}

              <DialogFooter className="flex sm:justify-between">
                <Button
                  disabled={isPending || !isTemplateSelected || deleteTemplateMutation.isPending}
                  type="button"
                  variant="ghost"
                  onClick={() => setIsDeleteDialogOpen(true)}
                >
                  {deleteTemplateMutation.isPending ? (
                    <>
                      <Spinner />
                      {t("pages.ai_parser.import_template_dialog.deleting")}
                    </>
                  ) : (
                    <>
                      <Trash2 className="size-4" />
                      {t("pages.ai_parser.import_template_dialog.delete")}
                    </>
                  )}
                </Button>

                <Button disabled={isPending || !isTemplateSelected} type="submit">
                  {isPending ? (
                    <>
                      <Spinner />
                      {t("pages.ai_parser.import_template_dialog.importing")}
                    </>
                  ) : (
                    <>
                      <Import className="size-4" />
                      {t("pages.ai_parser.import_template_dialog.import")}
                    </>
                  )}
                </Button>
              </DialogFooter>
            </form>
          </Form>
        </DialogContent>
      </Dialog>

      <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t("pages.ai_parser.import_template_dialog.delete_confirmation_title")}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t("pages.ai_parser.import_template_dialog.delete_confirmation_description")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={cancelDeleteTemplate}>
              {t("pages.ai_parser.import_template_dialog.delete_confirmation_cancel")}
            </AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive hover:bg-destructive/90"
              disabled={deleteTemplateMutation.isPending}
              onClick={deleteTemplate}
            >
              {deleteTemplateMutation.isPending ? (
                <>
                  <Spinner />
                  {t("pages.ai_parser.import_template_dialog.deleting")}
                </>
              ) : (
                t("pages.ai_parser.import_template_dialog.delete_confirmation_confirm")
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}
